/*
 * <emplifi-contact-form>
 * ----------------------
 * Custom contact form for Catalina Crunch.
 *
 *  - Gates all fields behind a required "Inquiries" selector.
 *  - Shows only the fields relevant to the chosen inquiry (separate <fieldset>s,
 *    disabled when hidden so their values are never collected or validated).
 *  - "Where did you purchase this product?" toggles the store fields.
 *  - Formats the phone number to ###-###-####, keeps Zip numeric, State 2-letter.
 *  - On submit, identifies the profile and fires a Klaviyo metric carrying every
 *    relevant field plus a ready-to-send `EmailBody`. A Klaviyo flow builds the
 *    plain-text Emplifi email from it.
 *  - Resets to a blank form on bfcache restore (browser Back button).
 *
 * No data is posted to Shopify.
 */
(function () {
  if (customElements.get('emplifi-contact-form')) return;

  // Maps the inquiry <select> value to the label Emplifi expects in the email.
  var INQUIRY_LABELS = {
    'report-product-issue': 'Report a Product Issue',
    'product-question': 'Product Question',
    'compliment': 'Share a Compliment',
    'general-inquiry': 'General Inquiry',
    'order-subscription-help': 'Order Subscription / Shipping Help'
  };

  // Per-inquiry email field order + the exact label Emplifi parses on.
  // [propertyKey, "Label"]. Only these fields appear in the email for each
  // inquiry; empty values are dropped (e.g. store fields on an online order).
  // NOTE: the option text shown to the customer (in the Liquid section) is
  // intentionally different from INQUIRY_LABELS above — keep them in sync by
  // their `data-inquiry` / value slugs.
  var EMAIL_FIELDS = {
    'report-product-issue': [
      ['FirstName', 'First Name'], ['LastName', 'Last Name'], ['Email', 'Email'], ['Phone', 'Phone'],
      ['Address1', 'Address_1'], ['Address2', 'Address_2'], ['City', 'City'], ['State', 'State'], ['Zip', 'Zip code'], ['Country', 'Country'],
      ['ProductName', 'Product Name'], ['Flavor', 'Flavor'], ['Size', 'Size'],
      ['UPCCode', 'UPC Code'], ['MfgCode', 'Mfg Code'], ['BestBuyDate', 'BEST BY Date'],
      ['PurchaseLocation', 'Where Purchased'],
      ['StoreName', 'Store'], ['StoreCity', 'Store_City'], ['StoreState', 'Store_State'],
      ['Comments', 'Comments'], ['CallRequested', 'Call_Requested']
    ],
    'product-question': [
      ['FirstName', 'First Name'], ['LastName', 'Last Name'], ['Email', 'Email'], ['Phone', 'Phone'],
      ['ProductName', 'Product Name'], ['Comments', 'Comments'], ['CallRequested', 'Call_Requested']
    ],
    'compliment': [
      ['FirstName', 'First Name'], ['LastName', 'Last Name'], ['Email', 'Email'],
      ['ProductName', 'Product Name'], ['Comments', 'Comments']
    ],
    'general-inquiry': [
      ['FirstName', 'First Name'], ['LastName', 'Last Name'], ['Email', 'Email'], ['Phone', 'Phone'],
      ['Comments', 'Comments']
    ],
    'order-subscription-help': [
      ['FirstName', 'First Name'], ['LastName', 'Last Name'], ['Email', 'Email'],
      ['OrderNumber', 'Order Number'], ['Comments', 'Comments']
    ]
  };

  // Flatten a value to a single clean line: CR/LF and the U+2028/U+2029 line and
  // paragraph separators collapse to a space (so one label:value pair stays on one
  // line), and other control characters are dropped. Uses numeric char codes to
  // avoid \u escapes in the source.
  function sanitizeValue(s) {
    var out = '';
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c === 13 || c === 10 || c === 0x2028 || c === 0x2029) {
        if (out.charAt(out.length - 1) !== ' ') out += ' ';
      } else if (c < 0x20 || c === 0x7f) {
        // drop control character
      } else {
        out += s.charAt(i);
      }
    }
    return out;
  }

  // Build the finished, ready-to-send Emplifi email body for this inquiry:
  // "Label: value" lines joined with CRLF, then JSON-escaped so Klaviyo can drop
  // it straight into the SendGrid webhook's text/plain value without breaking JSON.
  //
  // IMPORTANT downstream contract: the Klaviyo webhook body MUST interpolate this
  // value RAW — wrap it as {% autoescape off %}{{ event.EmailBody }}{% endautoescape %}.
  // If Klaviyo HTML-escapes it, the JSON escapes get mangled (\" -> \&quot;) and the
  // email is corrupted. Always verify with the webhook's "Preview / send test".
  function buildEmailBody(key, props) {
    var lines = ['Inquiry Type: ' + (INQUIRY_LABELS[key] || key)];
    (EMAIL_FIELDS[key] || []).forEach(function (pair) {
      var raw = props[pair[0]];
      if (raw == null) return;
      var val = sanitizeValue(String(raw)).trim();
      if (!val) return; // drop empty optional fields
      lines.push(pair[1] + ': ' + val);
    });
    return JSON.stringify(lines.join('\r\n')).slice(1, -1);
  }

  // Reformat an input's value in place while keeping the caret sensibly positioned,
  // by counting "significant" chars (digits/letters) before the caret.
  function formatInPlace(el, formatter, significant) {
    var start = el.selectionStart;
    if (start == null) { el.value = formatter(el.value); return; }
    var before = el.value.slice(0, start);
    var sig = 0;
    for (var i = 0; i < before.length; i++) if (significant.test(before[i])) sig++;
    el.value = formatter(el.value);
    var pos = 0, seen = 0;
    while (pos < el.value.length && seen < sig) {
      if (significant.test(el.value[pos])) seen++;
      pos++;
    }
    try { el.setSelectionRange(pos, pos); } catch (e) { /* unsupported input type */ }
  }

  function isEmail(v) {
    if (window.$s && typeof window.$s.validateEmail === 'function') {
      try { return window.$s.validateEmail(v); } catch (e) { /* fall through */ }
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  function digits(v) {
    return (v || '').replace(/\D/g, '');
  }

  function formatPhone(v) {
    var d = digits(v).slice(0, 10);
    if (d.length > 6) return d.slice(0, 3) + '-' + d.slice(3, 6) + '-' + d.slice(6);
    if (d.length > 3) return d.slice(0, 3) + '-' + d.slice(3);
    return d;
  }

  // Send to Klaviyo. Prefers the modern object API, falls back to the legacy
  // _learnq queue that the theme already uses for Viewed Product. Returns the
  // track promise on the modern path, or null on the legacy path.
  function fireKlaviyo(metric, props, identity) {
    try {
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        if (identity) window.klaviyo.identify(identity);
        return window.klaviyo.track(metric, props);
      }
    } catch (e) { /* fall through to legacy */ }

    var lq = (window._learnq = window._learnq || []);
    if (identity) {
      var legacy = {};
      if (identity.email) legacy.$email = identity.email;
      if (identity.first_name) legacy.$first_name = identity.first_name;
      if (identity.last_name) legacy.$last_name = identity.last_name;
      lq.push(['identify', legacy]);
    }
    lq.push(['track', metric, props]);
    return null;
  }

  // Debug mode is opt-in per page load via ?ecf_debug=1 (or =true) in the URL —
  // never persisted, so it can't accidentally ship enabled in production.
  function isDebugRequested() {
    try {
      var v = new URLSearchParams(window.location.search).get('ecf_debug');
      return v === '1' || v === 'true';
    } catch (e) {
      return false;
    }
  }

  class EmplifiContactForm extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.metricName = this.dataset.metricName || 'Submitted Contact Form';
      this.debug = isDebugRequested();
      this.form = this.querySelector('.ecf-form');
      this.select = this.querySelector('.ecf-inquiry');
      this.panels = Array.prototype.slice.call(this.querySelectorAll('.ecf-panel'));
      this.footer = this.querySelector('.ecf-footer');
      this.errorBox = this.querySelector('.ecf-error');
      this.successBox = this.querySelector('.ecf-success');
      this.submitBtn = this.querySelector('.ecf-submit');

      if (!this.form || !this.select) return;

      // Surface a visible banner when debug mode is active via ?ecf_debug=1.
      if (this.debug) {
        var banner = this.querySelector('.ecf-debug-banner');
        if (banner) banner.hidden = false;
      }

      // Programmatic required semantics for screen readers (form is novalidate).
      this.querySelectorAll('[data-required]').forEach((el) => {
        if (el.classList.contains('ecf-radio-group')) el.setAttribute('role', 'radiogroup');
        el.setAttribute('aria-required', 'true');
      });

      this.select.addEventListener('change', this.onInquiryChange.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));

      // Live formatting + clearing of error state as the user types.
      this.form.addEventListener('input', this.onInput.bind(this));
      this.form.addEventListener('change', this.onInput.bind(this));

      // Store-vs-online toggle.
      var toggle = this.querySelector('[data-store-toggle]');
      if (toggle) toggle.addEventListener('change', this.onStoreToggle.bind(this));

      // "Would you like us to call you?" → reveal the optional phone field.
      this.form.addEventListener('change', (e) => {
        var g = e.target && e.target.closest ? e.target.closest('[data-phone-toggle]') : null;
        if (g) this.syncPhoneGroup(g);
      });

      // Browser Back button (bfcache) → restore a pristine, blank form.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.resetAll();
      });
    }

    onInquiryChange() {
      var key = this.select.value;
      var activePanel = null;
      this.panels.forEach((panel) => {
        var active = panel.getAttribute('data-inquiry') === key;
        panel.hidden = !active;
        panel.disabled = !active;
        if (active) activePanel = panel;
      });
      // Keep the store group in sync with the purchase-location toggle.
      var toggle = this.querySelector('[data-store-toggle]');
      this.setStoreGroup(key === 'report-product-issue' && toggle && toggle.value === 'In a Store');
      // Keep the conditional phone field in sync with the call question.
      var callGroup = activePanel ? activePanel.querySelector('[data-phone-toggle]') : null;
      if (callGroup) this.syncPhoneGroup(callGroup);
      if (this.footer) this.footer.hidden = !key;
      this.clearErrors();
    }

    onStoreToggle(e) {
      this.setStoreGroup(e.target.value === 'In a Store');
    }

    setStoreGroup(show) {
      var group = this.querySelector('.ecf-store-group');
      if (!group) return;
      group.hidden = !show;
      group.disabled = !show;
      if (!show) {
        group.querySelectorAll('input').forEach((el) => {
          el.value = '';
          el.classList.remove('ecf-invalid');
          el.removeAttribute('aria-invalid');
        });
      }
    }

    // Reveal/hide the optional phone field based on its call-question radio group.
    syncPhoneGroup(callGroup) {
      var panel = callGroup.closest('.ecf-panel');
      if (!panel) return;
      var group = panel.querySelector('.ecf-phone-group');
      if (!group) return;
      var checked = callGroup.querySelector('input[type="radio"]:checked');
      var show = !!checked && checked.value === 'Yes';
      group.hidden = !show;
      group.disabled = !show;
      if (!show) {
        group.querySelectorAll('input').forEach((el) => {
          el.value = '';
          el.classList.remove('ecf-invalid');
          el.removeAttribute('aria-invalid');
        });
      }
    }

    onInput(e) {
      var el = e.target;
      if (!el || !el.getAttribute) return;
      var type = el.getAttribute('data-validate');
      if (type === 'phone') {
        formatInPlace(el, formatPhone, /\d/);
      } else if (type === 'state') {
        formatInPlace(el, function (v) { return v.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2); }, /[a-zA-Z]/);
      } else if (type === 'zip') {
        formatInPlace(el, function (v) { return digits(v).slice(0, 9); }, /\d/);
      }
      // Clear the invalid highlight once the user edits the field.
      el.classList.remove('ecf-invalid');
      el.removeAttribute('aria-invalid');
      var group = el.closest('.ecf-radio-group');
      if (group) { group.classList.remove('ecf-invalid'); group.removeAttribute('aria-invalid'); }
      var checkbox = el.closest('.ecf-checkbox');
      if (checkbox) checkbox.classList.remove('ecf-invalid');
    }

    // Active panel is driven by the select value (the source of truth), not by
    // scanning disabled-state, so it can't pick a stale panel if events race.
    activePanel() {
      var key = this.select.value;
      if (!key) return null;
      return this.querySelector('.ecf-panel[data-inquiry="' + key + '"]') || null;
    }

    validate(panel) {
      var messages = [];
      var addMsg = function (m) { if (messages.indexOf(m) === -1) messages.push(m); };
      var flag = function (el) { el.classList.add('ecf-invalid'); el.setAttribute('aria-invalid', 'true'); };

      // Required text / select fields (skip anything disabled via a hidden fieldset).
      panel.querySelectorAll('[data-required]').forEach((el) => {
        if (el.classList.contains('ecf-radio-group')) return;
        if (el.matches(':disabled')) return;
        if (!(el.value || '').trim()) {
          flag(el);
          addMsg('Please complete all the required fields.');
        }
      });

      // Required radio groups (e.g. "Would you like us to call you?").
      panel.querySelectorAll('.ecf-radio-group[data-required]').forEach((group) => {
        if (group.closest('fieldset:disabled')) return;
        if (!group.querySelector('input[type="radio"]:checked')) {
          flag(group);
          addMsg('Please answer all required questions.');
        }
      });

      // Format checks — only when the field has a value.
      panel.querySelectorAll('[data-validate]').forEach((el) => {
        if (el.matches(':disabled')) return;
        var v = (el.value || '').trim();
        if (!v) return;
        var type = el.getAttribute('data-validate');
        var ok = true;
        if (type === 'email') { ok = isEmail(v); if (!ok) addMsg('Please provide a valid email address.'); }
        else if (type === 'phone') { ok = digits(v).length === 10; if (!ok) addMsg('Please enter your phone number as ###-###-####.'); }
        else if (type === 'state') { ok = /^[A-Za-z]{2}$/.test(v); if (!ok) addMsg('Please enter the state as a 2-letter abbreviation.'); }
        else if (type === 'zip') { ok = /^\d{5}(\d{4})?$/.test(digits(v)); if (!ok) addMsg('Please enter a valid Zip Code (numbers only).'); }
        if (!ok) flag(el);
      });

      // Age verification (shared footer).
      var age = this.footer ? this.footer.querySelector('[data-age-verify]') : null;
      if (age && !age.checked) {
        var wrap = age.closest('.ecf-checkbox');
        if (wrap) wrap.classList.add('ecf-invalid');
        addMsg('Please confirm you are at least 13 years of age.');
      }

      return messages;
    }

    collect(panel) {
      var props = {};
      panel.querySelectorAll('[data-field]').forEach((el) => {
        var key = el.getAttribute('data-field');
        if (el.classList.contains('ecf-radio-group')) {
          if (el.closest('fieldset:disabled')) return; // a <div> never matches :disabled itself
          var checked = el.querySelector('input[type="radio"]:checked');
          props[key] = checked ? checked.value : ''; // blank if unanswered; buildEmailBody drops it
          return;
        }
        if (el.matches(':disabled')) return; // hidden store fields, etc.
        props[key] = (el.value || '').trim();
      });
      return props;
    }

    onSubmit(e) {
      e.preventDefault();
      if (this._submitting) return; // re-entrancy guard (blocks keyboard double-fire too)

      var panel = this.activePanel();
      if (!panel) {
        this.select.classList.add('ecf-invalid');
        this.showError('Please choose what we can help you with.');
        return;
      }

      var messages = this.validate(panel);
      if (messages.length) {
        this.showError(messages.join(' '));
        var firstBad = this.querySelector('.ecf-invalid');
        if (firstBad && firstBad.focus) firstBad.focus();
        return;
      }
      this.clearErrors();

      var data = this.collect(panel);
      var props = Object.assign({
        InquiryKey: this.select.value,
        InquiryType: INQUIRY_LABELS[this.select.value] || this.select.value,
        Source: 'Contact Us Page'
      }, data);
      props.AgeVerified = 'Yes';
      props.EmailBody = buildEmailBody(this.select.value, props);

      // Debug mode: show what would be sent and stop — nothing is sent to Klaviyo.
      if (this.debug) {
        var preview;
        try { preview = JSON.parse('"' + props.EmailBody + '"'); } catch (e) { preview = props.EmailBody; }
        window.alert(
          '[DEBUG] Klaviyo metric: ' + this.metricName + '\n' +
          '(debug mode is ON — nothing was sent)\n\n' +
          '=== Email body (decoded) ===\n' + preview + '\n\n' +
          '=== Event properties ===\n' + JSON.stringify(props, null, 2)
        );
        return;
      }

      var identity = {
        email: props.Email || undefined,
        first_name: props.FirstName || undefined,
        last_name: props.LastName || undefined
      };

      this._submitting = true;
      if (this.submitBtn) this.submitBtn.setAttribute('aria-busy', 'true');

      var self = this;
      var shown = false;
      var finish = function () {
        if (shown) return;
        shown = true;
        self.showSuccess();
      };

      var pending = null;
      try {
        pending = fireKlaviyo(this.metricName, props, identity);
      } catch (err) {
        /* Klaviyo failures should not block the confirmation. */
      }

      // Give the modern promise-based SDK a beat to flush, but never block the
      // user more than ~1.2s; the legacy queue path resolves immediately.
      if (pending && typeof pending.then === 'function') {
        pending.then(finish, finish);
        setTimeout(finish, 1200);
      } else {
        finish();
      }
    }

    showError(msg) {
      if (!this.errorBox) return;
      this.errorBox.textContent = msg;
      this.errorBox.hidden = false;
    }

    clearErrors() {
      if (this.errorBox) { this.errorBox.hidden = true; this.errorBox.textContent = ''; }
      this.querySelectorAll('.ecf-invalid').forEach((el) => {
        el.classList.remove('ecf-invalid');
        el.removeAttribute('aria-invalid');
      });
    }

    showSuccess() {
      this.form.hidden = true;
      if (this.successBox) {
        this.successBox.hidden = false;
        try { this.successBox.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* no-op */ }
        this.successBox.focus();
      }
      // Clear field values so a bfcache restore can't resubmit stale data.
      try { this.form.reset(); } catch (e) { /* no-op */ }
      if (this.submitBtn) this.submitBtn.removeAttribute('aria-busy');
    }

    // Restore the pristine, empty state (used on Back-button bfcache restore).
    resetAll() {
      try { this.form.reset(); } catch (e) { /* no-op */ }
      this.clearErrors();
      this._submitting = false;
      if (this.successBox) this.successBox.hidden = true;
      this.form.hidden = false;
      this.select.value = '';
      this.panels.forEach((panel) => { panel.hidden = true; panel.disabled = true; });
      this.setStoreGroup(false);
      this.querySelectorAll('.ecf-phone-group').forEach((g) => { g.hidden = true; g.disabled = true; });
      if (this.footer) this.footer.hidden = true;
      if (this.submitBtn) this.submitBtn.removeAttribute('aria-busy');
    }
  }

  customElements.define('emplifi-contact-form', EmplifiContactForm);
})();
